import { useRef, useEffect, useState } from 'react';
import { gsap } from 'gsap';
import './FlowingMenu.css';

// ─────────────────────────────────────────────────────────────
// v7.333 변경 요약 (원본 대비)
//   ① activeBgColor / activeTextColor / activeAccentColor prop 신설
//      활성 메뉴 색이 FlowingMenu.css 에 #00668a 로 박혀 있었다.
//      ⚠ v7.333 은 인라인 스타일로 덮으려 했는데 반만 먹혔다:
//        - 배경(background)은 !important 가 없어서 인라인이 이겼다 ✓
//        - 글자·아이콘은 `color: #00668a !important` 라서 인라인이 졌다 ✗
//        - 좌측 바(::before) / 우측 점(::after) 은 가상요소라 인라인이 닿지 않는다 ✗
//      → v7.334: CSS 쪽을 변수로 바꾸고, 여기서 그 변수를 꽂는다.
//        변수는 상속되므로 자식 요소와 가상요소까지 전부 따라온다.
//        prop 을 안 넘기면 CSS fallback(기존 청록)이 그대로 쓰인다.
//   ② 마퀴가 지나갈 때 원래 라벨을 숨긴다
//      마퀴는 메뉴 항목 '위를 덮고' 지나가는 별도 레이어다. 그래서 마퀴 배경이
//      반투명이면 밑에 깔린 원본 글자가 비쳐 'HDD/SSD 입고' 가 두 번 겹쳐 보였다.
//      (v7.326~v7.327 에서 알파를 0.5 → 0.95 로 올려 가리는 식으로 막아 뒀던 문제다)
//      → 마퀴가 올라오는 동안 버튼을 페이드 아웃한다. 이제 알파 50% 도 깔끔하다.
// ─────────────────────────────────────────────────────────────

// v7.334: '#rrggbb' → 'rgba(r,g,b,a)'. 점의 링·번짐 그림자를 강조색에서 파생시킨다.
//   CSS 만으로는 변수에 담긴 hex 에 알파를 입힐 수 없다 (color-mix 는 지원 폭이 걱정된다).
const fmRgba = (hex, a) => {
  let h = String(hex || '').trim().replace('#', '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const n = parseInt(h, 16);
  if (!Number.isFinite(n) || h.length !== 6) return `rgba(0,102,138,${a})`;
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

function FlowingMenu({
  items = [],
  speed = 15,
  textColor = '#0b1c30',
  bgColor = 'transparent',
  marqueeBgColor = '#00668a',
  marqueeTextColor = '#fff',
  borderColor = 'rgba(0,102,138,0.15)',
  activeBgColor = null,      // 활성 항목 배경
  activeTextColor = null,    // 활성 항목 글자·아이콘
  activeAccentColor = null   // 좌측 바 + 우측 점. 안 주면 activeTextColor 를 쓴다
}) {
  // CSS 변수는 상속된다 → wrap 한 곳에만 꽂으면 항목·가상요소까지 전부 닿는다.
  const wrapStyle = { backgroundColor: bgColor };
  if (activeBgColor) wrapStyle['--fm-active-bg'] = activeBgColor;
  if (activeTextColor) wrapStyle['--fm-active-fg'] = activeTextColor;
  const accent = activeAccentColor || activeTextColor;
  if (accent) {
    wrapStyle['--fm-active-accent'] = accent;
    wrapStyle['--fm-active-bar'] = `linear-gradient(180deg, ${accent} 0%, ${fmRgba(accent, 0.5)} 100%)`;
    wrapStyle['--fm-active-halo'] = fmRgba(accent, 0.18);
    wrapStyle['--fm-active-halo-2'] = fmRgba(accent, 0.10);
    wrapStyle['--fm-active-glow'] = fmRgba(accent, 0.35);
    wrapStyle['--fm-active-glow-2'] = fmRgba(accent, 0.5);
    wrapStyle['--fm-active-inset'] = fmRgba(accent, 0.12);
  }

  return (
    <div className="flowing-menu-wrap" style={wrapStyle}>
      <nav className="flowing-menu">
        {items.map((item, idx) => (
          <MenuItem
            key={item.id || idx}
            {...item}
            speed={speed}
            textColor={textColor}
            marqueeBgColor={marqueeBgColor}
            marqueeTextColor={marqueeTextColor}
            borderColor={borderColor}
            activeBgColor={activeBgColor}
            activeTextColor={activeTextColor}
          />
        ))}
      </nav>
    </div>
  );
}

function MenuItem({
  text, icon, onClick, active, speed,
  textColor, marqueeBgColor, marqueeTextColor, borderColor,
  activeBgColor, activeTextColor,
  // v7.348: 아코디언 — sub 는 한 단 들여쓴 하위 항목, expandable 은 펼침 화살표를 단 부모
  sub = false, expandable = false, expanded = false
}) {
  const itemRef = useRef(null);
  const linkRef = useRef(null);          // v7.333: 마퀴가 덮는 동안 숨길 원본 라벨
  const marqueeRef = useRef(null);
  const marqueeInnerRef = useRef(null);
  const animationRef = useRef(null);
  const [repetitions, setRepetitions] = useState(4);

  const animationDefaults = { duration: 0.6, ease: 'expo' };

  const findClosestEdge = (mouseX, mouseY, width, height) => {
    const topEdgeDist = distMetric(mouseX, mouseY, width / 2, 0);
    const bottomEdgeDist = distMetric(mouseX, mouseY, width / 2, height);
    return topEdgeDist < bottomEdgeDist ? 'top' : 'bottom';
  };

  const distMetric = (x, y, x2, y2) => {
    const xDiff = x - x2;
    const yDiff = y - y2;
    return xDiff * xDiff + yDiff * yDiff;
  };

  useEffect(() => {
    const calculateRepetitions = () => {
      if (!marqueeInnerRef.current) return;
      const marqueeContent = marqueeInnerRef.current.querySelector('.flowing-marquee__part');
      if (!marqueeContent) return;
      const contentWidth = marqueeContent.offsetWidth;
      if (contentWidth === 0) return;
      const viewportWidth = window.innerWidth;
      const needed = Math.ceil(viewportWidth / contentWidth) + 2;
      setRepetitions(Math.max(4, needed));
    };
    calculateRepetitions();
    window.addEventListener('resize', calculateRepetitions);
    return () => window.removeEventListener('resize', calculateRepetitions);
  }, [text, icon]);

  useEffect(() => {
    const setupMarquee = () => {
      if (!marqueeInnerRef.current) return;
      const marqueeContent = marqueeInnerRef.current.querySelector('.flowing-marquee__part');
      if (!marqueeContent) return;
      const contentWidth = marqueeContent.offsetWidth;
      if (contentWidth === 0) return;
      if (animationRef.current) animationRef.current.kill();
      animationRef.current = gsap.to(marqueeInnerRef.current, {
        x: -contentWidth, duration: speed, ease: 'none', repeat: -1
      });
    };
    const timer = setTimeout(setupMarquee, 50);
    return () => {
      clearTimeout(timer);
      if (animationRef.current) animationRef.current.kill();
    };
  }, [text, icon, repetitions, speed]);

  const handleMouseEnter = ev => {
    if (!itemRef.current || !marqueeRef.current || !marqueeInnerRef.current) return;
    const rect = itemRef.current.getBoundingClientRect();
    const x = ev.clientX - rect.left;
    const y = ev.clientY - rect.top;
    const edge = findClosestEdge(x, y, rect.width, rect.height);
    const tl = gsap
      .timeline({ defaults: animationDefaults })
      .set(marqueeRef.current, { y: edge === 'top' ? '-101%' : '101%' }, 0)
      .set(marqueeInnerRef.current, { y: edge === 'top' ? '101%' : '-101%' }, 0)
      .to([marqueeRef.current, marqueeInnerRef.current], { y: '0%' }, 0);
    // v7.333: 원본 라벨 페이드 아웃. 마퀴보다 조금 빠르게 빼야 겹쳐 보이는 순간이 없다.
    if (linkRef.current) tl.to(linkRef.current, { opacity: 0, duration: 0.25 }, 0);
  };

  const handleMouseLeave = ev => {
    if (!itemRef.current || !marqueeRef.current || !marqueeInnerRef.current) return;
    const rect = itemRef.current.getBoundingClientRect();
    const x = ev.clientX - rect.left;
    const y = ev.clientY - rect.top;
    const edge = findClosestEdge(x, y, rect.width, rect.height);
    const tl = gsap
      .timeline({ defaults: animationDefaults })
      .to(marqueeRef.current, { y: edge === 'top' ? '-101%' : '101%' }, 0)
      .to(marqueeInnerRef.current, { y: edge === 'top' ? '101%' : '-101%' }, 0);
    // 되돌릴 때는 마퀴가 물러난 뒤 나타나게 살짝 늦춘다
    if (linkRef.current) tl.to(linkRef.current, { opacity: 1, duration: 0.3 }, 0.15);
  };

  // v7.334: 배경은 CSS 변수(--fm-active-bg)로도 들어가지만 인라인으로도 한 번 더 건다.
  //   background 는 단축 속성이라 CSS 가 걸어 둔 background-image(그라디언트)까지 같이 지운다.
  //   글자·아이콘·바·점은 CSS 쪽 !important / 가상요소라 변수로만 제어된다 (위 wrapStyle 참고).
  const itemStyle = { borderColor };
  if (active && activeBgColor) {
    itemStyle.background = activeBgColor;
    itemStyle.backgroundImage = 'none';   // 명시적으로 한 번 더 (CSS 가 image 만 따로 걸었을 경우 대비)
  }
  if (active && activeTextColor) {
    // 자식(::after 등)이 currentColor 를 쓰는 경우까지 함께 따라오게 항목에도 색을 준다
    itemStyle.color = activeTextColor;
  }
  const linkColor = (active && activeTextColor) ? activeTextColor : textColor;

  return (
    <div
      className={`flowing-menu__item${active ? ' flowing-menu__item--active' : ''}${sub ? ' flowing-menu__item--sub' : ''}`}
      ref={itemRef} style={itemStyle}>
      <button
        type="button"
        ref={linkRef}
        className="flowing-menu__item-link"
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ color: linkColor }}
      >
        {icon && <span className="material-symbols-outlined flowing-menu__item-icon" style={{ fontSize: sub ? 17 : 20 }}>{icon}</span>}
        <span>{text}</span>
        {/* v7.348: 부모 항목의 펼침 화살표. 항목 자체가 가운데 정렬이라 absolute 로 오른쪽에 붙인다. */}
        {expandable && (
          <span className="material-symbols-outlined flowing-menu__item-chevron"
            style={{ fontSize: 18, transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>expand_more</span>
        )}
      </button>
      <div className="flowing-marquee" ref={marqueeRef} style={{ backgroundColor: marqueeBgColor }}>
        <div className="flowing-marquee__inner-wrap">
          <div className="flowing-marquee__inner" ref={marqueeInnerRef} aria-hidden="true">
            {[...Array(repetitions)].map((_, idx) => (
              <div className="flowing-marquee__part" key={idx} style={{ color: marqueeTextColor }}>
                <span>{text}</span>
                {icon && <span className="material-symbols-outlined flowing-marquee__icon" style={{ fontSize: 18 }}>{icon}</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FlowingMenu;
